if (!customElements.get('sf-decision-companion')) {
  class SplitflaskDecisionCompanion extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.productInfo = this.closest('product-info');
      this.quantityBundles = this.productInfo?.querySelector('sf-quantity-bundles');
      this.accessoryPackage = this.productInfo?.querySelector('sf-accessory-package');
      this.productForm = this.productInfo?.querySelector('product-form');
      this.form = this.productForm?.querySelector('form');
      this.primaryButton = this.productInfo?.querySelector('[id^="ProductSubmitButton-"]');
      this.primaryLabel = this.primaryButton?.querySelector(':scope > span');
      this.productImage = this.querySelector('.sf-decision-companion__product-image');
      this.accessory = this.querySelector('[data-sf-companion-accessory]');
      this.accessoryImage = this.querySelector('.sf-decision-companion__accessory-image');
      this.quantityBadge = this.querySelector('[data-sf-companion-quantity-badge]');
      this.titleElement = this.querySelector('[data-sf-companion-title]');
      this.colorElement = this.querySelector('[data-sf-companion-color]');
      this.quantityElement = this.querySelector('[data-sf-companion-quantity]');
      this.setupElement = this.querySelector('[data-sf-companion-setup]');
      this.savingsElement = this.querySelector('[data-sf-companion-savings]');
      this.savingsText = this.savingsElement?.querySelector('span');
      this.stageElement = this.querySelector('[data-sf-companion-stage]');
      this.statusElement = this.querySelector('[data-sf-companion-status]');
      this.cta = this.querySelector('[data-sf-companion-cta]');
      this.ctaLabel = this.querySelector('[data-sf-companion-cta-label]');
      this.variants = this.readJson('[data-all-variants]');
      this.currentVariant = this.readJson('[data-selected-variant]', {});
      this.bundleQuantity = Number(this.quantityBundles?.currentQuantity || 1);
      this.bundleDiscount = Number(this.quantityBundles?.currentDiscount || 0);
      this.bundleSavings = Number(this.quantityBundles?.currentSavings || 0);
      this.accessoryUnitPrice = Number(this.dataset.accessoryUnitPrice || 0);
      this.currency = this.dataset.currency || 'USD';
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.primaryButtonVisible = false;
      this.currentStage = 'color';

      if (!this.productInfo || !this.productImage) return;

      this.productInfo.addEventListener('change', this.onProductChange, true);
      this.accessoryPackage?.addEventListener('change', this.onProductChange);
      this.productInfo.addEventListener('sf:bundle-change', this.onBundleChange);
      this.cta?.addEventListener('click', this.onCtaClick);
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange, { passive: true });

      this.variantChangeUnsubscriber = typeof subscribe === 'function'
        ? subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange)
        : null;

      if (this.primaryButton) {
        this.primaryButtonObserver = new IntersectionObserver(this.onPrimaryButtonIntersection, {
          threshold: 0.1,
        });
        this.primaryButtonObserver.observe(this.primaryButton);
      }

      if (this.primaryLabel) {
        this.primaryLabelObserver = new MutationObserver(this.queueUpdate);
        this.primaryLabelObserver.observe(this.primaryLabel, {
          childList: true,
          characterData: true,
          subtree: true,
        });
      }

      this.update();
      this.updateStage();
    }

    disconnectedCallback() {
      this.productInfo?.removeEventListener('change', this.onProductChange, true);
      this.accessoryPackage?.removeEventListener('change', this.onProductChange);
      this.productInfo?.removeEventListener('sf:bundle-change', this.onBundleChange);
      this.cta?.removeEventListener('click', this.onCtaClick);
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      this.variantChangeUnsubscriber?.();
      this.primaryButtonObserver?.disconnect();
      this.primaryLabelObserver?.disconnect();
      if (this.updateFrame) cancelAnimationFrame(this.updateFrame);
      if (this.viewportFrame) cancelAnimationFrame(this.viewportFrame);
      this.initialized = false;
    }

    readJson(selector, fallback = []) {
      try {
        const source = this.productInfo?.querySelector(selector)?.textContent;
        return source ? JSON.parse(source) : fallback;
      } catch (error) {
        return fallback;
      }
    }

    onVariantChange = (event) => {
      const detail = event?.data;
      if (!detail || String(detail.sectionId) !== String(this.dataset.sectionId) || !detail.variant) return;
      this.currentVariant = detail.variant;
      this.queueUpdate();
    };

    onBundleChange = (event) => {
      this.bundleQuantity = Math.max(1, Number(event.detail?.quantity || 1));
      this.bundleDiscount = Math.max(0, Number(event.detail?.discount || 0));
      this.bundleSavings = Math.max(0, Number(event.detail?.savings || 0));
      this.queueUpdate();
    };

    onProductChange = (event) => {
      if (!event.target.matches(
        '[data-sf-bundle-choice], [data-sf-color-select], [data-sf-package-choice], [data-sf-accessory-variant], product-variant-selects input, product-variant-selects select'
      )) return;
      this.update();
    };

    onPrimaryButtonIntersection = ([entry]) => {
      this.primaryButtonVisible = entry.isIntersecting;
      this.updateCta();
    };

    onCtaClick = () => {
      if (!this.primaryButton || this.primaryButton.disabled) return;
      this.primaryButton.click();
    };

    onViewportChange = () => {
      if (this.viewportFrame) return;
      this.viewportFrame = requestAnimationFrame(() => {
        this.viewportFrame = null;
        this.updateStage();
      });
    };

    queueUpdate = () => {
      if (this.updateFrame) return;
      this.updateFrame = requestAnimationFrame(() => {
        this.updateFrame = null;
        this.update();
      });
    };

    getSelectedVariant() {
      const selectedId = Number(this.form?.querySelector('[name="id"]')?.value || this.currentVariant?.id || 0);
      return this.variants.find((variant) => Number(variant.id) === selectedId)
        || this.currentVariant
        || this.variants[0]
        || null;
    }

    getBundleState() {
      const checkedChoice = this.quantityBundles?.querySelector('[data-sf-bundle-choice]:checked');
      const quantity = Math.max(1, Number(
        this.quantityBundles?.currentQuantity || checkedChoice?.value || this.bundleQuantity || 1
      ));
      const selectedTier = this.quantityBundles?.querySelector(
        `[data-sf-bundle-tier][data-quantity="${quantity}"]`
      );
      const discount = Math.max(0, Number(
        this.quantityBundles?.currentDiscount ?? selectedTier?.dataset.discount ?? this.bundleDiscount ?? 0
      ));
      const savings = Math.max(0, Number(
        this.quantityBundles?.currentSavings ?? this.bundleSavings ?? 0
      ));
      return { quantity, discount, savings };
    }

    getColorLabel(variant, quantity) {
      if (quantity > 1 && this.quantityBundles) {
        const colors = Array.from(this.quantityBundles.querySelectorAll('[data-sf-color-select]'))
          .slice(0, quantity)
          .map((select) => select.selectedOptions[0]?.textContent?.trim())
          .filter(Boolean);
        if (colors.length) {
          const uniqueColors = new Set(colors.map((color) => color.toLowerCase()));
          return uniqueColors.size > 1 ? this.dataset.mixedColorsLabel : colors[0];
        }
      }
      return variant?.public_title || variant?.title || this.dataset.initialColor || '';
    }

    getPackageState() {
      const packageSelected = this.accessoryPackage?.querySelector(
        '[data-sf-package-choice][value="package"]'
      )?.checked === true;
      const accessoryInput = this.accessoryPackage?.querySelector('[data-sf-accessory-variant]:checked');
      return {
        selected: packageSelected,
        input: accessoryInput,
        image: accessoryInput?.dataset.variantImage || this.accessoryImage?.src || '',
        color: accessoryInput?.dataset.optionValue || '',
      };
    }

    getVariantImage(variant) {
      const raw = variant?.featured_image?.src
        || variant?.featured_media?.preview_image?.src
        || this.dataset.initialImage
        || '';
      if (!raw) return '';
      try {
        const url = new URL(raw.startsWith('//') ? `${location.protocol}${raw}` : raw, location.origin);
        url.searchParams.set('width', '900');
        return url.href;
      } catch (error) {
        return raw.startsWith('//') ? `${location.protocol}${raw}` : raw;
      }
    }

    update() {
      const variant = this.getSelectedVariant();
      const { quantity, discount, savings } = this.getBundleState();
      const packageState = this.getPackageState();
      const color = this.getColorLabel(variant, quantity);
      const setup = packageState.selected ? this.dataset.packageLabel : this.dataset.cupLabel;
      const unit = quantity === 1 ? this.dataset.unitSingular : this.dataset.unitPlural;
      const imageUrl = this.getVariantImage(variant);

      this.bundleQuantity = quantity;
      this.bundleDiscount = discount;
      this.bundleSavings = savings;

      if (this.colorElement) this.colorElement.textContent = color;
      if (this.quantityElement) this.quantityElement.textContent = `${quantity} ${unit}`;
      if (this.setupElement) this.setupElement.textContent = setup;
      if (this.quantityBadge) this.quantityBadge.textContent = `×${quantity}`;
      if (this.titleElement) this.titleElement.textContent = `${color} · ${setup}`;

      this.updateProductImage(imageUrl, variant, color);
      this.updateAccessory(packageState);
      this.updateSavings(savings, packageState.selected);
      this.updateCta();

      if (this.statusElement) {
        const accessoryColor = packageState.selected && packageState.color ? `, ${packageState.color} handle` : '';
        this.statusElement.textContent = `Setup updated: ${color}, ${quantity} ${unit}, ${setup}${accessoryColor}.`;
      }
    }

    updateProductImage(imageUrl, variant, color) {
      if (!imageUrl || this.productImage.src === imageUrl) return;
      this.classList.add('is-updating');
      const preload = new Image();
      preload.onload = () => {
        if (!this.isConnected) return;
        this.productImage.removeAttribute('srcset');
        this.productImage.src = imageUrl;
        this.productImage.alt = variant?.featured_image?.alt || `${this.dataset.productTitle}, ${color}`;
        requestAnimationFrame(() => this.classList.remove('is-updating'));
      };
      preload.onerror = () => this.classList.remove('is-updating');
      preload.src = imageUrl;
    }

    updateAccessory(packageState) {
      if (!this.accessory) return;
      const showAccessory = packageState.selected && Boolean(packageState.image);
      this.accessory.classList.toggle('is-hidden', !showAccessory);
      this.accessory.setAttribute('aria-hidden', showAccessory ? 'false' : 'true');

      if (showAccessory && this.accessoryImage && packageState.image) {
        const imageUrl = packageState.image.startsWith('//')
          ? `${location.protocol}${packageState.image}`
          : packageState.image;
        if (this.accessoryImage.src !== imageUrl) {
          this.accessoryImage.removeAttribute('srcset');
          this.accessoryImage.src = imageUrl;
        }
        this.accessoryImage.alt = packageState.color
          ? `${packageState.color} comfort-grip handle`
          : 'Comfort-grip handle';
      }
    }

    updateSavings(bundleSavings, packageSelected) {
      if (!this.savingsElement || !this.savingsText) return;
      const accessoryDiscount = Math.max(0, Number(this.dataset.accessoryDiscount || 0));
      const accessoryQuantity = this.dataset.accessoryQuantity === 'one' ? 1 : this.bundleQuantity;
      const accessorySavings = packageSelected
        ? Math.floor(this.accessoryUnitPrice * accessoryDiscount / 100) * accessoryQuantity
        : 0;
      const totalSavings = Math.max(0, Number(bundleSavings || 0)) + accessorySavings;
      this.savingsText.textContent = totalSavings > 0 ? `You save ${this.formatMoney(totalSavings)}` : '';
      this.savingsElement.hidden = totalSavings <= 0;
    }

    formatMoney(cents) {
      if (typeof formatPrice === 'function') return formatPrice(cents, this.moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.currency,
      }).format(Number(cents || 0) / 100);
    }

    updateCta() {
      if (!this.cta) return;
      const enabled = this.dataset.showConditionalCta === 'true';
      const shouldShow = enabled && !this.primaryButtonVisible;
      const disabled = !this.primaryButton || this.primaryButton.disabled;
      this.cta.classList.toggle('is-visible', shouldShow);
      this.cta.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
      this.cta.tabIndex = shouldShow ? 0 : -1;
      this.cta.disabled = disabled;
      if (this.ctaLabel && this.primaryLabel?.textContent.trim()) {
        this.ctaLabel.textContent = this.primaryLabel.textContent.trim();
      }
    }

    updateStage() {
      if (window.innerWidth < 990) return;
      const headerHeight = Number.parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--header-height')
      ) || 76;
      const focusLine = headerHeight + Math.min(300, window.innerHeight * .38);
      const candidates = [
        ['color', this.productInfo?.querySelector('.product__variant-picker')],
        ['quantity', this.quantityBundles],
        ['setup', this.accessoryPackage],
        ['purchase', this.primaryButton?.closest('.product__buy-buttons')],
      ].filter(([, element]) => element && element.offsetParent !== null);

      if (!candidates.length) return;
      let nearest = candidates[0];
      let nearestDistance = Number.POSITIVE_INFINITY;

      candidates.forEach((candidate) => {
        const rect = candidate[1].getBoundingClientRect();
        const distance = focusLine >= rect.top && focusLine <= rect.bottom
          ? 0
          : Math.min(Math.abs(focusLine - rect.top), Math.abs(focusLine - rect.bottom));
        if (distance < nearestDistance) {
          nearest = candidate;
          nearestDistance = distance;
        }
      });

      const stage = nearest[0];
      if (stage === this.currentStage) return;
      this.currentStage = stage;
      this.dataset.stage = stage;
      const labels = {
        color: this.dataset.colorStageLabel || 'Color',
        quantity: this.dataset.quantityStageLabel || 'Quantity',
        setup: this.dataset.setupStageLabel || 'Setup',
        purchase: this.dataset.readyStageLabel || 'Ready',
      };
      if (this.stageElement) this.stageElement.textContent = labels[stage];
      this.querySelectorAll('[data-sf-companion-summary]').forEach((item) => {
        const summaryStage = item.dataset.sfCompanionSummary;
        item.classList.toggle('is-active', summaryStage === stage || (stage === 'purchase' && summaryStage === 'setup'));
      });
    }
  }

  customElements.define('sf-decision-companion', SplitflaskDecisionCompanion);
}
